export const PLAN_TYPE_KEYS = ['bp', 'glucose', 'weight', 'custom'];

export const PLAN_TYPE_ACCENTS = {
  bp: '#9e2028',
  glucose: '#c2560f',
  weight: '#41618a',
  custom: '#1B2A4A',
};

export const BP_TECHNIQUE = [
  'Rest quietly for 5 minutes before you measure.',
  'Sit with your back supported and both feet flat on the floor.',
  'Rest your arm on a table so the cuff is at heart level.',
  'Put the cuff on a bare arm, not over clothing.',
  'Do not talk, and stay still while the cuff inflates.',
  'Avoid caffeine, exercise, and tobacco for 30 minutes beforehand.',
  'Take two readings one minute apart — the monitor records both.',
];

export const GLU_TECHNIQUE = [
  'Wash and dry your hands before testing.',
  'Use a fresh lancet and test strip each time.',
  'Test fasting readings before eating or drinking anything but water.',
  'For a CGM, keep the sensor site clean and dry.',
  'Note anything unusual — illness, stress, or a missed meal.',
];

export const WT_TECHNIQUE = [
  'Weigh at the same time each morning, after using the bathroom.',
  'Use the same scale on a hard, flat floor.',
  'Wear similar clothing (or none) each time.',
  'Tell your care team about a big change and how you feel.',
];

export const FIELD_LABELS = {
  systolic: 'Target systolic',
  diastolic: 'Target diastolic',
  basis: 'Basis',
  reviewDate: 'Next review',
  fastingLow: 'Fasting low',
  fastingHigh: 'Fasting high',
  postMeal: 'Post-meal target',
  a1c: 'A1c goal',
  targetWeight: 'Target weight',
  dayGain: 'Daily gain alert',
  weekGain: 'Weekly gain alert',
  title: 'Plan title',
  focus: 'Focus',
  metric: 'Target metric',
  target: 'Target value',
  description: 'Goal description',
  device: 'Device',
  schedule: 'Schedule',
  requiredDaysPer30: 'Required days / 30',
  missedReadingAlert: 'Missed-reading alert',
  escalation: 'Escalation rules',
};

const defaultReviewDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export function getDefaultReviewDate() {
  return defaultReviewDate();
}

export const PLAN_TYPES = {
  bp: {
    key: 'bp',
    label: 'Blood Pressure',
    blurb: 'Track blood pressure and pulse with alert bands and a BP goal.',
    planTitle: 'Hypertension Care Plan',
    accent: PLAN_TYPE_ACCENTS.bp,
    condition: { code: '', display: '' },
    scale: {
      min: 100,
      max: 190,
      segs: [
        ['Normal', 'sg'],
        ['Elevated', 'sa'],
        ['Stage 1', 'so'],
        ['Stage 2 +', 'sr'],
      ],
      axis: ['100', '120', '130', '140', '180+'],
    },
    deviceOptions: ['Cellular upper-arm BP monitor', 'BP monitor + weight scale'],
    scheduleOptions: ['Twice daily, morning and evening', 'Once daily, morning'],
    gapOptions: ['2 days', '3 days', '5 days'],
    basisOptions: [
      'Standard adult target',
      'Diabetes or chronic kidney disease',
      'Age 65+ relaxed target',
      'Provider-specified',
    ],
    defaults: {
      systolic: 130,
      diastolic: 80,
      basis: 'Standard adult target',
      device: 'Cellular upper-arm BP monitor',
      schedule: 'Twice daily, morning and evening',
      requiredDaysPer30: 16,
      missedReadingAlert: '3 days',
    },
    interventions: [
      ['Daily transmitted BP review', 'Care manager reviews the trend dashboard each business day'],
      ['Monthly interactive contact', 'At least one live call or video visit per calendar month'],
      ['Medication reconciliation', 'Review prescriptions, OTC drugs, and supplements monthly'],
      ['Sodium reduction counseling', 'Target under 1,500 mg daily; label-reading and cooking strategies'],
      ['DASH eating pattern', 'Fruits, vegetables, whole grains, low-fat dairy; dietitian referral if needed'],
      ['Physical activity plan', 'Build toward 150 minutes of moderate activity per week'],
      ['Weight management', 'Reduce by 5 to 10 percent of body weight over six months'],
      ['Low pulse follow-up', 'Reassess rate-limiting medication given a low pulse reading'],
    ],
    education: [
      ['Personal BP target', 'What the numbers mean and the goal we are working toward'],
      ['Hypertension is symptom-free', 'Treat the number, not how you feel'],
      ['Correct cuff technique', 'Rest five minutes, back supported, feet flat, arm at heart level'],
      ['Confirming a transmission', 'How to check a reading reached the care team'],
      ['Medication routine', 'Timing, purpose, and what to do about a missed dose'],
      ['Side effects to report', 'Which symptoms warrant a call to the care team'],
      ['When to call 911', 'Crisis readings with chest pain, weakness, speech or vision change'],
    ],
    emergency:
      'Call 911 if a high reading comes with chest pain, shortness of breath, weakness or numbness on one side, trouble speaking, vision changes, a severe headache, or confusion.',
  },
  glucose: {
    key: 'glucose',
    label: 'Blood Glucose',
    blurb: 'Monitor blood sugar with fasting, post-meal, and A1c targets.',
    planTitle: 'Blood Glucose Care Plan',
    accent: PLAN_TYPE_ACCENTS.glucose,
    condition: { code: '', display: '' },
    scale: {
      min: 60,
      max: 260,
      segs: [
        ['Low', 'sr'],
        ['In range', 'sg'],
        ['Elevated', 'sa'],
        ['High', 'sr'],
      ],
      axis: ['60', '80', '130', '180', '260'],
    },
    deviceOptions: ['Cellular glucometer', 'Continuous glucose monitor (CGM)'],
    scheduleOptions: ['Before meals and bedtime', 'Fasting each morning', 'Twice daily', 'As directed'],
    gapOptions: ['2 days', '3 days', '5 days'],
    basisOptions: ['Type 2 — standard', 'Type 1', 'Gestational', 'Provider-specified'],
    defaults: {
      fastingLow: 80,
      fastingHigh: 130,
      postMeal: 180,
      a1c: 7,
      basis: 'Type 2 — standard',
      device: 'Cellular glucometer',
      schedule: 'Fasting each morning',
      requiredDaysPer30: 16,
      missedReadingAlert: '3 days',
    },
    interventions: [
      ['Daily transmitted glucose review', 'Care manager reviews readings each business day'],
      ['Monthly interactive contact', 'At least one live call or video visit per month'],
      ['Medication / insulin adherence', 'Confirm doses, timing, and technique monthly'],
      ['Carbohydrate counting support', 'Coach on carb awareness and portion sizing'],
      ['Hypoglycemia action plan', 'Confirm fast-carb supply and the 15-15 rule'],
      ['Physical activity plan', 'Build toward 150 minutes of moderate activity per week'],
      ['A1c recheck scheduled', 'Order per provider, typically every 3 months'],
      ['Foot and eye exam reminders', 'Confirm annual screenings are up to date'],
    ],
    education: [
      ['Recognizing highs and lows', 'Symptoms of hyper- and hypoglycemia'],
      ['Glucometer / CGM technique', 'Correct testing and sensor use'],
      ['Carbohydrate awareness', 'Reading labels and estimating carbs'],
      ['Sick-day rules', 'Managing glucose during illness'],
      ['Medication and insulin timing', 'When and how to take each medicine'],
      ['Foot care basics', 'Daily checks and when to call'],
      ['When to call 911', 'Severe lows, vomiting, or confusion'],
    ],
    emergency:
      'Call 911 for a very low reading you cannot treat, or a high reading with vomiting, confusion, trouble breathing, or fruity-smelling breath.',
  },
  weight: {
    key: 'weight',
    label: 'Weight',
    blurb: 'Daily weigh-ins with rapid-gain alerts for fluid monitoring.',
    planTitle: 'Weight Care Plan',
    accent: PLAN_TYPE_ACCENTS.weight,
    condition: { code: '', display: '' },
    scale: null,
    deviceOptions: ['Cellular weight scale', 'Scale + BP monitor'],
    scheduleOptions: ['Daily, morning after voiding', 'Every other day', 'Weekly'],
    gapOptions: ['2 days', '3 days', '5 days'],
    basisOptions: [
      'Weight management',
      'Heart failure fluid monitoring',
      'Post-operative',
      'Provider-specified',
    ],
    defaults: {
      targetWeight: 180,
      basis: 'Weight management',
      dayGain: 3,
      weekGain: 5,
      device: 'Cellular weight scale',
      schedule: 'Daily, morning after voiding',
      requiredDaysPer30: 20,
      missedReadingAlert: '2 days',
    },
    interventions: [
      ['Daily transmitted weight review', 'Care manager reviews the trend each business day'],
      ['Monthly interactive contact', 'At least one live call or video visit per month'],
      ['Sodium and fluid guidance', 'Coach on limits for fluid retention'],
      ['Diuretic adherence', 'Confirm timing and response'],
      ['Edema / symptom check', 'Ask about swelling and breathlessness at each contact'],
      ['Nutrition counseling', 'Refer to dietitian as needed'],
      ['Physical activity plan', 'Build activity as tolerated'],
    ],
    education: [
      ['How and when to weigh', 'Same time daily, after voiding, same scale and clothing'],
      ['Recognizing fluid gain', 'What rapid weight gain and swelling mean'],
      ['Sodium and fluid limits', 'Practical ways to cut salt and track fluids'],
      ['Medication adherence', 'Why the diuretic and its timing matter'],
      ['When to call the team', 'Gain thresholds and warning symptoms'],
    ],
    emergency:
      'Call 911 for severe shortness of breath, chest pain, or swelling with trouble breathing.',
  },
  custom: {
    key: 'custom',
    label: 'Custom',
    blurb: 'Define your own metric, schedule, and escalation rules.',
    planTitle: 'Custom Care Plan',
    accent: PLAN_TYPE_ACCENTS.custom,
    condition: { code: '', display: '' },
    scale: null,
    deviceOptions: [],
    scheduleOptions: ['Twice daily', 'Once daily', 'Weekly'],
    gapOptions: ['2 days', '3 days', '5 days'],
    basisOptions: [],
    defaults: {
      title: '',
      focus: '',
      metric: '',
      target: '',
      description: '',
      device: '',
      schedule: 'Twice daily',
      requiredDaysPer30: 16,
      missedReadingAlert: '3 days',
      escalation: '',
    },
    interventions: [],
    education: [],
    emergency: 'Call 911 for any severe or life-threatening symptoms.',
  },
};

export function getPlanType(typeKey) {
  return PLAN_TYPES[typeKey] || PLAN_TYPES.custom;
}

/** Offer assigned vital types plus Custom. */
export function getAvailablePlanTypeKeys({ bp = false, bg = false, weight = false } = {}) {
  const keys = [];
  if (bp) keys.push('bp');
  if (bg) keys.push('glucose');
  if (weight) keys.push('weight');
  keys.push('custom');
  return keys;
}

export function checklistFromConfig(items) {
  return (items || []).map(([label, desc]) => ({
    label,
    desc: desc || '',
    checked: false,
    custom: false,
  }));
}

export function buildDefaultForm(typeKey) {
  const t = getPlanType(typeKey);
  return {
    typeKey,
    reviewDate: getDefaultReviewDate(),
    ...t.defaults,
    scheduleIsCustom: false,
    scheduleCustom: '',
    gapIsCustom: false,
    gapCustom: '',
    interventions: checklistFromConfig(t.interventions),
    education: checklistFromConfig(t.education),
    notes: '',
    approvedBy: '__pending__',
  };
}
